import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { portfolioOverviewService } from './portfolioOverview.service';
import { handleThunkError } from '../helpers/thunkError';

const initialState = {
  totalValue: 0,
  clientPortfolios: [],
  portfolioAccounts: [],

  selectedPortfolioName: '',
  selectedAccountIds: [],

  loadingPortfolioOverview: false,
  loadingPortfolioAccounts: false,

  error: null,
  portfolioAccountsError: null
};

export const getPortfolioOverview = createAsyncThunk(
  'portfolioOverview/getPortfolioOverview',
  async ({ clientId, fromDate, toDate }, thunkAPI) => {
    try {
      return await portfolioOverviewService.getPortfolioOverview({
        clientId,
        fromDate,
        toDate
      });
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch portfolio overview');
    }
  }
);

export const getPortfolioAccounts = createAsyncThunk(
  'portfolioOverview/getPortfolioAccounts',
  async ({ accountIds, date }, thunkAPI) => {
    try {
      return await portfolioOverviewService.getPortfolioAccounts({
        accountIds,
        date
      });
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch portfolio accounts');
    }
  }
);

const portfolioOverviewSlice = createSlice({
  name: 'portfolioOverview',

  initialState,

  reducers: {
    setSelectedPortfolio: (state, action) => {
      state.selectedPortfolioName = action.payload?.portfolioName ?? '';

      state.selectedAccountIds = action.payload?.accountIds ?? [];
    },

    clearSelectedPortfolio: (state) => {
      state.selectedPortfolioName = '';
      state.selectedAccountIds = [];
      state.portfolioAccounts = [];
      state.portfolioAccountsError = null;
    },

    clearPortfolioOverview: (state) => {
      state.totalValue = 0;
      state.clientPortfolios = [];
      state.portfolioAccounts = [];
      state.selectedPortfolioName = '';
      state.selectedAccountIds = [];
      state.error = null;
      state.portfolioAccountsError = null;
    }
  },

  extraReducers: (builder) => {
    builder

      // -----------------------------------------
      // Portfolio Overview
      // -----------------------------------------
      .addCase(getPortfolioOverview.pending, (state) => {
        state.loadingPortfolioOverview = true;

        // Clear old data immediately so it cannot
        // be displayed while the new request is loading.
        state.totalValue = 0;
        state.clientPortfolios = [];

        state.error = null;
      })

      .addCase(getPortfolioOverview.fulfilled, (state, action) => {
        state.loadingPortfolioOverview = false;

        state.totalValue = action.payload?.totalValue ?? 0;

        state.clientPortfolios = action.payload?.clientPortfolios ?? [];
      })

      .addCase(getPortfolioOverview.rejected, (state, action) => {
        state.loadingPortfolioOverview = false;

        // Make sure stale data is not left behind
        // if the request fails.
        state.totalValue = 0;
        state.clientPortfolios = [];

        state.error = action.payload;
      })

      // -----------------------------------------
      // Portfolio Accounts
      // -----------------------------------------
      .addCase(getPortfolioAccounts.pending, (state) => {
        state.loadingPortfolioAccounts = true;

        // Clear old account data immediately.
        state.portfolioAccounts = [];

        state.portfolioAccountsError = null;
      })

      .addCase(getPortfolioAccounts.fulfilled, (state, action) => {
        state.loadingPortfolioAccounts = false;

        state.portfolioAccounts = action.payload ?? [];
      })

      .addCase(getPortfolioAccounts.rejected, (state, action) => {
        state.loadingPortfolioAccounts = false;

        // Do not leave previous account data
        // visible when the request fails.
        state.portfolioAccounts = [];

        state.portfolioAccountsError = action.payload;
      });
  }
});

export const { setSelectedPortfolio, clearSelectedPortfolio, clearPortfolioOverview } =
  portfolioOverviewSlice.actions;

export default portfolioOverviewSlice.reducer;
